import React, { useState, useEffect } from "react";
import nprogress from "nprogress";
import {
  mailbotsAdminBrowser,
  getAuthorizedAccounts,
  setAccessToken
} from "./lib/utils";
import { Link, useNavigate, useParams } from "react-router-dom";
import _ from "lodash";
import mailbotsHero from "./images/mailbotsHero.png";
import futLogo from "./images/futLogo.svg";
import Loading from "./Loading";
import { SelectTimezone } from "./SelectTimezone";
import { message as antMessage } from "antd";

const Confirm = ({ loggedInUser, isTrustedMailBot }) => {
  const navigate = useNavigate();
  const params = useParams();

  const [state, setState] = useState({
    mailbot: {},
    mailbotIsTrusted: false,
    userIsValidated: null,
    form: {},
    mailbotLoaded: false,
    userLoaded: false
  });

  const fullyLoaded = () => {
    return state.mailbotLoaded && state.userLoaded;
  };

  const redirectIfAlreadyInstalled = (mailbot) => {
    if (mailbot.installed) {
      antMessage.success(`${mailbot.name} is installed and ready for use`);
      navigate(`/skills/${mailbot.id}/settings`);
    }
  };

  const loadMailBot = async (mailbotid) => {
    try {
      const res = await mailbotsAdminBrowser.getMailBot({ mailbotid });
      redirectIfAlreadyInstalled(res.mailbot);
      const mailbotIsTrusted = isTrustedMailBot(
        res.mailbot.oauth2_client_id
      );
      setState(prev => ({
        ...prev,
        mailbot: res.mailbot || {},
        mailbotIsTrusted,
        mailbotLoaded: true
      }));
    } catch (e) {
      antMessage.error("Error getting mailbot");
      setState(prev => ({ ...prev, mailbotLoaded: true }));
      console.log(e);
      return false;
    }
  };

  const userAlreadyValidated = (user) => {
    return Boolean(user && user.accepted_terms);
  };

  // componentDidMount
  useEffect(() => {
    const mailbotId = params.mailbotid;
    if (mailbotId) {
      loadMailBot(mailbotId);
    } else {
      antMessage.error(
        "Sorry, something went wrong. Did you add the complete link from the email?"
      );
    }
  }, [params.mailbotid]);

  // Monitor when user object has been loaded
  useEffect(() => {
    if (loggedInUser) {
      setState(prev => ({
        ...prev,
        userLoaded: true
      }));
    }
  }, [loggedInUser]);

  // Auto-check forms box and change message if user has already validated
  useEffect(() => {
    if (userAlreadyValidated(loggedInUser)) {
      setState(prev => ({
        ...prev,
        form: {
          ...prev.form,
          terms: true
        }
      }));
      console.log("user is validated, setting checkbox");
    }
  }, [loggedInUser]);

  // If ext trusted and user is validated, send directly to install
  useEffect(() => {
    if (
      userAlreadyValidated(loggedInUser) &&
      state.mailbotIsTrusted
    ) {
      window.location.href = state.mailbot.install_url;
    }
  }, [loggedInUser, state.mailbotIsTrusted, state.mailbot.install_url]);

  const handleOnChange = (event) => {
    let type = event.target.type;
    const form = { ...state.form };
    switch (type) {
      case "checkbox":
        form[event.target.name] = event.target.checked;
        break;
      default:
        form[event.target.name] = event.target.value;
        break;
    }

    setState(prev => ({
      ...prev,
      form
    }));
  };

  /**
   * TODO: De-Duplicate – this also exists in Register Component
   * Workaround to capture fields the user did not touch,
   * (ie, the timezone option), thus not firing an onChange event.
   */
  const captureWholeForm = function () {
    return new Promise((resolve, reject) => {
      let fields = document.getElementsByClassName("mailbots-data");
      let form = {};
      for (let prop in fields) {
        let k = fields[prop].name;
        let v = fields[prop].value;
        if (k && v) {
          form[k] = v;
        }
        let newForm = Object.assign({}, state.form, form);
        setState(prev => ({
          ...prev,
          form: { ...newForm }
        }));
        resolve();
      }
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    await captureWholeForm();
    try {
      if (!state.form.terms)
        throw new Error("Please accept the terms before continuing");
      let updateRes = await mailbotsAdminBrowser.userUpdateSelf({
        user: {
          timezone: state.form.timezone,
          accepted_terms: state.form.terms
        }
      });
      const redirect =
        state.mailbot.install_url || `/skills/${params.mailbotid}`;
      window.location.href = redirect;
    } catch (e) {
      console.error(e);
      antMessage.error(e.message);
    }
  };

  const mailbotName = (
    <span style={{ whiteSpace: "nowrap" }}>
      {state.mailbot.name || ""}
    </span>
  );

  return (
    <div className="container-fluid" id="content" style={{ paddingTop: 30 }}>
      {fullyLoaded() ? (
        <div style={{ marginBottom: 200 }}>
          <section className="row">
            <div className="col-md-5 col-md-offset-4 col-sm-8 col-sm-offset-2 text-center">
              <img
                src={futLogo}
                alt="MailBots Logo"
                style={{ width: 150, marginBottom: 20 }}
              />

              <br />

              {state.mailbot.icon && (
                <span style={{ whiteSpace: "nowrap" }}>
                  <img
                    src={state.mailbot.icon}
                    alt={`${state.mailbot.name} Icon`}
                    style={{ height: 75, marginBottom: 20 }}
                  />
                </span>
              )}
              {state.mailbot.name && false && (
                <div className="text-left" style={{ marginBottom: 100 }}>
                  <div className="panel-body">
                    <img
                      src={state.mailbot.icon}
                      alt={`Icon for ${state.mailbot.name}`}
                      style={{ float: "left", margin: 15, width: 150 }}
                    />
                    <h2>{state.mailbot.name}</h2>
                    <p>By: {state.mailbot.developer_name}</p>
                    <p>
                      {state.mailbot.description.slice(0, 90)}{" "}
                      <a
                        href={`/skills/${state.mailbot.id}`}
                        target="_blank"
                      >
                        more...
                      </a>
                    </p>
                  </div>
                </div>
              )}

              <div className="text-left">
                <div className="row">
                  <div className="col-sm-12" style={{ maxWidth: 700 }}>
                    {userAlreadyValidated(loggedInUser) ? (
                      <div>
                        <p className="lead">
                          You are about to be redirected away from MailBots to{" "}
                          {mailbotName}, developed by{" "}
                          {state.mailbot.developer_name}. Please make
                          sure you trust this developer before continuing.
                        </p>
                        <button
                          type="submit"
                          className="btn btn-primary btn-mailbots btn-block"
                          onClick={() =>
                            (window.location.href =
                              state.mailbot.install_url)
                          }
                        >
                          Continue
                        </button>
                      </div>
                    ) : (
                      <div>
                        <p className="lead" style={{ marginTop: 60 }}>
                          {mailbotName} was built using{" "}
                          <a
                            href="https://www.mailbots.com"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            MailBots,
                          </a>{" "}
                          a platform for creating and managing email-bots.
                          After confirming your MailBots account, you will be
                          redirected to {mailbotName} by{" "}
                          {state.mailbot.developer_name}.
                        </p>

                        <form method="post" onSubmit={handleSubmit}>
                          <fieldset>
                            {!userAlreadyValidated() && (
                              <div>
                                <label>Confirm your timezone</label>
                                <div className="form-group">
                                  <SelectTimezone
                                    onChange={handleOnChange}
                                    className="mailbots-data"
                                  />
                                </div>
                                <label
                                  className="form-check"
                                  htmlFor="terms"
                                  style={{ fontWeight: "normal" }}
                                >
                                  <span style={{ marginTop: 5 }}>
                                    <input
                                      type="checkbox"
                                      style={{ margin: "0 7px 20px 0" }}
                                      required
                                      defaultChecked={state.form.terms}
                                      onChange={handleOnChange}
                                      name="terms"
                                      id="terms"
                                    />
                                  </span>
                                  I agree to MailBot 's{" "}
                                  <a
                                    className="muted"
                                    href="https://www.followupthen.com/privacy"
                                  >
                                    privacy policy
                                  </a>{" "}
                                  and{" "}
                                  <a
                                    className="muted"
                                    href="https://www.followupthen.com/terms"
                                  >
                                    terms of service
                                  </a>
                                  .
                                </label>
                                <button
                                  type="submit"
                                  className="btn btn-primary btn-mailbots btn-block"
                                >
                                  Continue
                                </button>
                              </div>
                            )}
                          </fieldset>
                        </form>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      ) : (
        <Loading />
      )}
    </div>
  );
};

export default Confirm;
